import React from "react";
import { FiDroplet, FiAlertCircle } from "react-icons/fi";
import { MiniBar, StatusBadge, statusColor } from "./waterHelpers";

const TYPE_ICON = { Municipal: "🏙", Borewell: "⛏", Rainwater: "🌧", Recycled: "♻" };

const WmSourcePanel = ({ d }) => {
  const totalDependency = d.sources.reduce((a, s) => a + s.dependencyPct, 0);

  return (
    <div className="wm-sources">

      {/* ── Source Cards ── */}
      <div className="ws-cards-grid">
        {d.sources.map(src => (
          <div key={src.id} className={`ws-card ${src.status === "Offline" ? "ws-card-offline" : ""}`}>
            <div className="ws-card-header">
              <div className="ws-card-icon" style={{ background: `${src.color}18`, color: src.color }}>
                {TYPE_ICON[src.type] || "💧"}
              </div>
              <div style={{ flex: 1 }}>
                <div className="ws-card-name">{src.name}</div>
                <StatusBadge status={src.status} />
              </div>
              <div className="ws-dependency-pct" style={{ color: src.color }}>{src.dependencyPct}%</div>
            </div>

            {src.type === "Municipal" && (
              <div className="ws-metrics">
                <div className="ws-metric-row"><span>Daily Quota</span><strong>{(src.dailyQuota/1000).toFixed(0)} KL</strong></div>
                <div className="ws-metric-row"><span>Today Received</span><strong style={{ color: src.color }}>{(src.todayReceived/1000).toFixed(1)} KL</strong></div>
                <MiniBar value={src.todayReceived} max={src.dailyQuota} color={src.color} height={7} />
                <div className="ws-metric-row"><span>Pressure</span><strong>{src.pressurePsi} PSI</strong></div>
                <div className="ws-metric-row"><span>TDS</span><strong>{src.qualityTds} ppm · pH {src.qualityPh}</strong></div>
                <div className="ws-metric-row"><span>Outages this month</span>
                  <strong style={{ color: src.outagesThisMonth > 2 ? "#ef4444" : "#10b981" }}>{src.outagesThisMonth}</strong>
                </div>
              </div>
            )}

            {src.type === "Borewell" && src.status !== "Offline" && (
              <div className="ws-metrics">
                <div className="ws-metric-row"><span>Daily Yield</span><strong>{(src.dailyYield/1000).toFixed(0)} KL</strong></div>
                <div className="ws-metric-row"><span>Today Extracted</span><strong style={{ color: src.color }}>{(src.todayExtracted/1000).toFixed(1)} KL</strong></div>
                <MiniBar value={src.todayExtracted} max={src.dailyYield} color={src.color} height={7} />
                <div className="ws-metric-row"><span>Water Level</span><strong>{src.waterLevel}%</strong></div>
                <div className="ws-metric-row"><span>Depth</span><strong>{src.depthFt} ft</strong></div>
                <div className="ws-metric-row"><span>TDS</span><strong>{src.qualityTds} ppm · pH {src.qualityPh}</strong></div>
                <div className="ws-metric-row"><span>Power Used</span><strong>{src.powerUsed} kWh</strong></div>
              </div>
            )}

            {src.type === "Borewell" && src.status === "Offline" && (
              <div className="ws-offline-note">
                <FiAlertCircle size={16} color="#ef4444" />
                {src.offlineReason}
              </div>
            )}

            {src.type === "Rainwater" && (
              <div className="ws-metrics">
                <div className="ws-metric-row"><span>Collected This Month</span><strong style={{ color: src.color }}>{(src.collectedThisMonth/1000).toFixed(0)} KL</strong></div>
                <div className="ws-metric-row"><span>Current Storage</span><strong>{(src.storedCurrent/1000).toFixed(1)} KL · {src.storagePct}%</strong></div>
                <MiniBar value={src.storagePct} color={src.color} height={7} />
                <div className="ws-metric-row"><span>Last Rain</span><strong>{src.lastRainEvent}</strong></div>
              </div>
            )}

            {src.type === "Recycled" && (
              <div className="ws-metrics">
                <div className="ws-metric-row"><span>Daily Production</span><strong>{(src.dailyProduction/1000).toFixed(0)} KL</strong></div>
                <div className="ws-metric-row"><span>Today Produced</span><strong style={{ color: src.color }}>{(src.todayProduced/1000).toFixed(1)} KL</strong></div>
                <MiniBar value={src.todayProduced} max={src.dailyProduction} color={src.color} height={7} />
                <div className="ws-metric-row"><span>TDS</span><strong>{src.qualityTds} ppm</strong></div>
                <div className="ws-metric-row"><span>Used For</span><strong>{src.usedFor}</strong></div>
                <div className="ws-metric-row"><span>Cost Savings</span><strong style={{ color: "#10b981" }}>₹{src.savings}/day</strong></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Source Dependency + Forecast ── */}
      <div className="ws-bottom-row">

        <div className="ws-dep-card">
          <div className="ws-card-title">Source Dependency Ratio</div>
          <div className="ws-dep-bars">
            {d.sources.map(src => src.dependencyPct > 0 && (
              <div key={src.id} className="ws-dep-row">
                <span className="ws-dep-label">{src.name.split(" ")[0]} {src.name.split(" ")[1] || ""}</span>
                <div style={{ flex: 1 }}>
                  <MiniBar value={src.dependencyPct} color={src.color} height={14} />
                </div>
                <span className="ws-dep-pct" style={{ color: src.color }}>{src.dependencyPct}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ws-forecast-card">
          <div className="ws-card-title"><FiDroplet size={14} /> Water Availability Forecast</div>
          <div className="ws-forecast-stat">
            <div className="ws-fs-val" style={{ color: d.summary.daysOfWaterRemaining < 7 ? "#ef4444" : "#10b981" }}>
              {d.summary.daysOfWaterRemaining} days
            </div>
            <div className="ws-fs-label">Estimated water remaining</div>
          </div>
          <div className="ws-forecast-detail">
            <div className="ws-fd-row"><span>Total Available</span><strong>{(d.summary.totalWaterAvailable/1000).toFixed(0)} KL</strong></div>
            <div className="ws-fd-row"><span>Daily Avg Consumption</span><strong>{(d.summary.todayConsumption/1000).toFixed(1)} KL</strong></div>
            <div className="ws-fd-row"><span>All Sources Online?</span>
              <strong style={{ color: d.sources.every(s => s.status !== "Offline") ? "#10b981" : "#f59e0b" }}>
                {d.sources.filter(s => s.status === "Active").length} / {d.sources.length} Active
              </strong>
            </div>
          </div>
          <div className="ws-forecast-ai">
            💡 {d.summary.aiInsights[1]}
          </div>
        </div>

      </div>
    </div>
  );
};

export default WmSourcePanel;
